import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, Calendar, FileText, Clock, Bell, MapPin, 
  Mail, Phone, Briefcase, Award, ChevronRight, Plus,
  AlertTriangle, CheckCircle, Building
} from 'lucide-react';
import { format, differenceInDays, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function MyProfile() {
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [scadenze, setScadenze] = useState([]);
  const [documenti, setDocumenti] = useState([]);
  const [appuntamenti, setAppuntamenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profilo');

  useEffect(() => {
    fetchMyData();
  }, []);

  const fetchMyData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      // Get current user info
      const meRes = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!meRes.ok) {
        navigate('/');
        return;
      }
      
      const userData = await meRes.json();
      
      if (userData.employee_id) {
        // Fetch employee data
        const empRes = await fetch(`${API_URL}/api/employees/${userData.employee_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (empRes.ok) {
          setEmployee(await empRes.json());
        }

        // Fetch personal scadenze
        const scadenzeRes = await fetch(`${API_URL}/api/my-scadenze`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (scadenzeRes.ok) {
          setScadenze(await scadenzeRes.json());
        }

        // Fetch personal documents
        const docRes = await fetch(`${API_URL}/api/employees/${userData.employee_id}/documents`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (docRes.ok) {
          setDocumenti(await docRes.json());
        }

        // Fetch personal appointments
        const appRes = await fetch(`${API_URL}/api/my-appointments`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (appRes.ok) {
          setAppuntamenti(await appRes.json());
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getUrgencyColor = (daysUntil) => {
    if (daysUntil < 0) return 'bg-red-500 text-white';
    if (daysUntil <= 7) return 'bg-red-100 text-red-700 border-red-300';
    if (daysUntil <= 30) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-green-100 text-green-700 border-green-300';
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">Caricamento...</div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="card text-center py-12">
          <User size={64} className="mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Profilo non disponibile</h2>
          <p className="text-muted-foreground">
            Il tuo account non è ancora collegato a un profilo dipendente.
            Contatta l'amministratore.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="card mb-6">
        <div className="flex flex-col md:flex-row items-center gap-6">
          {employee.foto_url ? (
            <img
              src={`${API_URL}${employee.foto_url}`}
              alt={`${employee.nome} ${employee.cognome}`}
              className="w-32 h-32 rounded-full object-cover ring-4 ring-primary/20"
            />
          ) : (
            <div className="w-32 h-32 rounded-full bg-primary/20 flex items-center justify-center text-4xl font-bold text-primary">
              {employee.nome?.charAt(0)}{employee.cognome?.charAt(0)}
            </div>
          )}
          
          <div className="text-center md:text-left flex-1">
            <h1 className="text-3xl font-bold">
              {employee.nome} {employee.cognome}
            </h1>
            {employee.ruolo && (
              <p className="text-lg text-muted-foreground mt-1">{employee.ruolo}</p>
            )}
            <div className="flex flex-wrap gap-4 mt-3 justify-center md:justify-start">
              {employee.sede && (
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Building size={16} className="text-primary" />
                  {employee.sede}
                </span>
              )}
              {employee.email && (
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Mail size={16} />
                  {employee.email}
                </span>
              )}
              {employee.telefono && (
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Phone size={16} />
                  {employee.telefono}
                </span>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="flex gap-4">
            <div className="text-center px-4 py-2 bg-muted rounded-lg">
              <p className="text-2xl font-bold text-primary">{scadenze.filter(s => differenceInDays(parseISO(s.data_scadenza), new Date()) <= 30).length}</p>
              <p className="text-xs text-muted-foreground">Scadenze Prossime</p>
            </div>
            <div className="text-center px-4 py-2 bg-muted rounded-lg">
              <p className="text-2xl font-bold text-primary">{appuntamenti.length}</p>
              <p className="text-xs text-muted-foreground">Appuntamenti</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border mb-6">
        <div className="flex gap-4">
          {[
            { id: 'profilo', label: 'Il Mio Profilo', icon: User },
            { id: 'scadenze', label: 'Le Mie Scadenze', icon: Clock },
            { id: 'documenti', label: 'I Miei Documenti', icon: FileText },
            { id: 'calendario', label: 'Il Mio Calendario', icon: Calendar },
          ].map(tab => (
            <button
              key={tab.id}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'profilo' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Dati Personali */}
          <div className="card">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <User size={20} className="text-primary" />
              Dati Personali
            </h3>
            <div className="space-y-3">
              {[
                { label: 'Nome', value: employee.nome },
                { label: 'Cognome', value: employee.cognome },
                { label: 'Codice Fiscale', value: employee.codice_fiscale },
                { label: 'Data di Nascita', value: employee.data_nascita ? format(new Date(employee.data_nascita), 'dd/MM/yyyy') : null },
                { label: 'Luogo di Nascita', value: employee.luogo_nascita },
                { label: 'Indirizzo', value: employee.indirizzo },
              ].map((item, idx) => item.value && (
                <div key={idx} className="flex justify-between py-2 border-b border-border last:border-0">
                  <span className="text-muted-foreground">{item.label}</span>
                  <span className="font-medium">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Dati Lavorativi */}
          <div className="card">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Briefcase size={20} className="text-primary" />
              Dati Lavorativi
            </h3>
            <div className="space-y-3">
              {[
                { label: 'Matricola', value: employee.numero_matricola },
                { label: 'Ruolo', value: employee.ruolo },
                { label: 'Qualifica', value: employee.qualifica },
                { label: 'Tipo Contratto', value: employee.tipo_contratto },
                { label: 'Data Assunzione', value: employee.data_assunzione ? format(new Date(employee.data_assunzione), 'dd/MM/yyyy') : null },
                { label: 'Sede', value: employee.sede },
              ].map((item, idx) => item.value && (
                <div key={idx} className="flex justify-between py-2 border-b border-border last:border-0">
                  <span className="text-muted-foreground">{item.label}</span>
                  <span className="font-medium">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'scadenze' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg">Le Mie Scadenze HSE</h3>
          </div>
          
          {scadenze.length === 0 ? (
            <div className="card text-center py-12">
              <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
              <p className="text-muted-foreground">Nessuna scadenza da monitorare</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {scadenze.map((scad) => {
                const daysUntil = differenceInDays(parseISO(scad.data_scadenza), new Date());
                
                return (
                  <div 
                    key={scad._id}
                    className={`card border-l-4 ${getUrgencyColor(daysUntil)}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold">{scad.tipo}</h4>
                        <p className="text-sm text-muted-foreground">{scad.descrizione}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">
                          {format(parseISO(scad.data_scadenza), 'dd MMM yyyy', { locale: it })}
                        </p>
                        <p className={`text-sm ${daysUntil < 0 ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>
                          {daysUntil < 0 
                            ? `Scaduto da ${Math.abs(daysUntil)} giorni!`
                            : daysUntil === 0 
                            ? 'Scade oggi!'
                            : `Tra ${daysUntil} giorni`
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'documenti' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg">I Miei Documenti</h3>
          </div>
          
          {documenti.length === 0 ? (
            <div className="card text-center py-12">
              <FileText size={48} className="mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nessun documento caricato</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {documenti.map((doc) => (
                <a
                  key={doc._id}
                  href={`${API_URL}/api/download/${doc._id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="card flex items-center gap-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <FileText size={24} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{doc.nome_file}</p>
                    <p className="text-sm text-muted-foreground">
                      {doc.categoria || 'Documento'} • 
                      {doc.created_at && format(new Date(doc.created_at), ' dd/MM/yyyy')}
                    </p>
                  </div>
                  <ChevronRight size={20} className="text-muted-foreground" />
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'calendario' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg">I Miei Appuntamenti</h3>
            <p className="text-sm text-muted-foreground">
              Corsi, visite mediche e altri impegni pianificati dal tuo responsabile HSE
            </p>
          </div>
          
          {appuntamenti.length === 0 ? (
            <div className="card text-center py-12">
              <Calendar size={48} className="mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nessun appuntamento in programma</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {appuntamenti.map((app) => {
                const appDate = new Date(app.data_inizio);
                const isPast = appDate < new Date();
                
                return (
                  <div 
                    key={app._id}
                    className={`card ${isPast ? 'opacity-60' : ''}`}
                  >
                    <div className="flex gap-4">
                      <div className={`w-16 text-center py-2 rounded-lg ${
                        isPast ? 'bg-muted' : 'bg-primary text-white'
                      }`}>
                        <p className="text-xs uppercase">
                          {format(appDate, 'MMM', { locale: it })}
                        </p>
                        <p className="text-2xl font-bold">
                          {format(appDate, 'd')}
                        </p>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            app.tipo === 'visita_medica' ? 'bg-red-100 text-red-700' :
                            app.tipo === 'corso' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {app.tipo === 'visita_medica' ? '🏥 Visita Medica' :
                             app.tipo === 'corso' ? '📚 Corso' :
                             '📅 Appuntamento'}
                          </span>
                          {isPast && <span className="text-xs text-muted-foreground">(Passato)</span>}
                        </div>
                        <h4 className="font-semibold">{app.titolo}</h4>
                        <p className="text-sm text-muted-foreground">
                          {format(appDate, 'HH:mm', { locale: it })}
                          {app.location && ` • 📍 ${app.location}`}
                        </p>
                        {app.note && (
                          <p className="text-sm mt-2 p-2 bg-muted rounded">
                            {app.note}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MyProfile;
